export enum UnitCategory {
  Length = "Length",
  Area = "Area",
  Volume = "Volume",
  Pressure = "Pressure",
  Temperature = "Temperature",
  Mass = "Mass",
  FlowRateLiquid = "FlowRateLiquid",
  FlowRateGas = "FlowRateGas",
  Permeability = "Permeability"
}

export enum UnitSystem {
  Hybrid = 0,
  SI = 1,
  Imperial = 2
}

export const BASE_UNITS: Record<UnitCategory, string> = {
  [UnitCategory.Length]: "m",
  [UnitCategory.Area]: "m2",
  [UnitCategory.Volume]: "m3",
  [UnitCategory.Pressure]: "kPa",
  [UnitCategory.Temperature]: "C",
  [UnitCategory.Mass]: "kg",
  [UnitCategory.FlowRateLiquid]: "m3/d",
  [UnitCategory.FlowRateGas]: "e3m3/d",
  [UnitCategory.Permeability]: "mD"
};

export const DISPLAY_UNITS: Record<
  UnitCategory,
  Record<keyof typeof UnitSystem, string>
> = {
  [UnitCategory.Length]: { Hybrid: "m", SI: "m", Imperial: "ft" },
  [UnitCategory.Area]: { Hybrid: "m2", SI: "m2", Imperial: "ft2" },
  [UnitCategory.Volume]: { Hybrid: "m3", SI: "m3", Imperial: "bbl" },
  [UnitCategory.Pressure]: { Hybrid: "kPa", SI: "kPa", Imperial: "psi" },
  [UnitCategory.Temperature]: { Hybrid: "C", SI: "C", Imperial: "F" },
  [UnitCategory.Mass]: { Hybrid: "kg", SI: "kg", Imperial: "lb" },
  [UnitCategory.FlowRateLiquid]: { Hybrid: "m3/d", SI: "m3/d", Imperial: "bbl/d" },
  [UnitCategory.FlowRateGas]: { Hybrid: "e3m3/d", SI: "e3m3/d", Imperial: "Mcf/d" },
  [UnitCategory.Permeability]: { Hybrid: "mD", SI: "mD", Imperial: "mD" }
};

/**
 * Unit identifiers for programmatic use in conversions
 */
export const UNIT_IDS = {
  // Length
  METERS: "m",
  KILOMETERS: "km",
  FEET: "ft",
  MILES: "mi",

  // Area
  SQUARE_METERS: "m2",
  SQUARE_KILOMETERS: "km2",
  SQUARE_FEET: "ft2",
  SQUARE_MILES: "mi2",

  // Volume
  CUBIC_METERS: "m3",
  CUBIC_FEET: "ft3",

  // Flow Rate
  CUBIC_METERS_PER_MIN: "m3/min",
  CUBIC_FEET_PER_MIN: "ft3/min",

  // Mass
  TONNES: "t",
  TONS: "ton",

  // Proppant Intensity
  TONNE_PER_METER: "t/m",
  LB_PER_FOOT: "lb/ft",

  // Pressure
  KILOPASCALS: "kPa",
  PSI: "psi",

  // Density
  KG_PER_CUBIC_METER: "kg/m3",
  LB_PER_CUBIC_FOOT: "lb/ft3"
} as const;

/**
 * Unit labels for display
 */
export const UNIT_LABELS = {
  // Length
  METERS: "m",
  KILOMETERS: "km",
  FEET: "ft",
  MILES: "mi",

  // Area
  SQUARE_METERS: "m²",
  SQUARE_KILOMETERS: "km²",
  SQUARE_FEET: "ft²",
  SQUARE_MILES: "mi²",

  // Volume
  CUBIC_METERS: "m³",
  CUBIC_FEET: "ft³",

  // Flow Rate
  CUBIC_METERS_PER_MIN: "m³/min",
  CUBIC_FEET_PER_MIN: "ft³/min",

  // Mass
  TONNES: "t",
  TONS: "ton",

  // Pressure
  KILOPASCALS: "kPa",
  PSI: "psi",

  // Density
  KG_PER_CUBIC_METER: "kg/m³",
  LB_PER_CUBIC_FOOT: "lb/ft³"
};

/**
 * Result interface for unit conversion hooks
 */
export interface UnitConversionResult {
  isReady: boolean;

  /**
   * Convert a value FROM base unit TO display unit
   * Use this for displaying stored values to the user
   * @param valueInBaseUnit - Value in the base/storage unit
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Value in the user's preferred display unit
   */
  convert: (valueInBaseUnit: number, precision?: number) => number;

  /**
   * Convert a value FROM display unit TO base unit
   * Use this for converting user input back to storage units
   * @param valueInDisplayUnit - Value in the user's display unit
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Value in the base/storage unit
   */
  convertBack: (valueInDisplayUnit: number, precision?: number) => number;

  /**
   * Convert a value between any two units (direct WASM function)
   * @param fromUnit - Source unit (e.g., "m2", "km2")
   * @param toUnit - Target unit (e.g., "ft2", "mi2")
   * @param value - Value to convert
   * @param precision - Optional precision (decimal places). Pass -1 for no rounding, or 0+ for decimal places. Default: -1 (no rounding)
   * @returns Converted value
   */
  convertUnitToUnit: (
    fromUnit: string,
    toUnit: string,
    value: number,
    precision?: number
  ) => number;

  /**
   * Get the display unit string for the current category and user preference
   * @returns Unit string (e.g., "m", "ft", "kPa", "psi")
   */
  getDisplayUnit: () => string;

  /**
   * Get the base (storage) unit string for the current category
   * @returns Base unit string (e.g., "m", "kPa")
   */
  getBaseUnit: () => string;

  /**
   * The user's unit preference type (0=Hybrid, 1=SI, 2=Imperial)
   */
  unitType: number;
}
